import React, { useContext, useEffect, useMemo, useState } from 'react';
import { getLocale, shallowEqual, useIntl, useSelector } from 'umi';
import moment from 'moment';
import { TableColumn } from 'tea-component';
import { formatExeclCommon, formatFilterValues, formatNum, getChartDateFormatString, getDynamicLocalCnEnText, getSystemColor, handleDownload } from '@/pages/dashboard/utils/utils';
import CardBox from '@/pages/dashboard/components/CardBox';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import { useOption, useWeekDay } from '@/pages/dashboard/utils/customHook';
import { getMtxRevenueDistribution } from '@/pages/dashboard/services/monetization';
import getSystemLegend from '@/pages/dashboard/components/CountryRegionRowItem/CountryTabChartsItem/utils';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { useMemoizedFn } from 'ahooks';
import { getCustomValueColumnTooltip } from '@/pages/dashboard/components/EchartsTooltip/utils';
import { LANGUAGE_EN } from '@/pages/dashboard/_config/constant';

// 格式化横坐标 label
const axisLabelFormat = (opt, timeType) => opt.xAxis?.map((item) => ({
  ...item,
  axisLabel: {
    ...item.axisLabel,
    formatter: (value) => getChartDateFormatString(timeType, value),
  },
}));

const formatterFunc = (params, timeType = 'Day') => 
  // 走新浮窗处理
  getCustomValueColumnTooltip({
    params, options: {
      hasSum: false,
      hasShare: false,
      valueNameCn: '',
      valueNameEn: '',
      isEn: getLocale() === LANGUAGE_EN,
      isPlatform: true,
      valueFormatRule: '',
      formatFirstThText: (date) => moment(`${date}`).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD')
    }
  }, (value) => formatNum(value));
  // return getEchartTooltipText({
  //   title: moment(`${params[0].axisValue}`).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD'),
  //   sumName: formatMessage({ id: 'monetization_revenue_Revenue' }),
  //   // data: [...params]
  //   // .sort((f, s) => s.value - f.value)
  //   data: params.map(({ seriesName, value, color }) => ({
  //     name: seriesName,
  //     metricsNum: formatNum(value),
  //     customColor: true,
  //     icon: getSystemFlag(seriesName) || '',
  //     color,
  //   })),
  //   isSort: true
  // })




function PcRevenueChannelDistribution() {
  // const { filterLoading, formValue, gameCode } = props
  const contextObj = useContext(FilterContext);
  const { filterVal: formValue, filterLoading, } = contextObj || {};
  const { timeType = 'Day' } = formValue || {};
  const { gameCode, selector } = useSelector((state: any) => ({
    gameCode: state.dashboard?.currentGame?.game_code,
    selector: state?.dashboardSetting?.selector ?? {},
  }), shallowEqual);
  // console.log(formValue,contextObj,selector);
  const { getWeekDay } = useWeekDay();
  const { formatMessage } = useIntl();
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);


  const getRevenueData = useMemoizedFn(() => {
    const { timeType, rangeTime, ...filters } = formValue;
    // const { rangeTime, ...filters } = filterValue
    const tabNames: any = {
      Day: 'daily',
      Week: 'weekly',
      Month: 'monthly'
    };
    const params =
    {
      game_code: gameCode,
      start_time: moment(rangeTime?.[0]).format('YYYY-MM-DD'),
      end_time: moment(rangeTime?.[1]).format('YYYY-MM-DD'),
      period: tabNames[timeType] || 'daliy',
      filters: formatFilterValues(filters),
      accumulation: {
        by_system: true,
      }
    };
    setLoading(true);
    getMtxRevenueDistribution(params).then((result: any) => {
      setLoading(false);
      setError(false);
      setRevenueData(result);
    }).catch(() => {
      setError(true);
      setLoading(false);
    });
  });
  // 点击筛选器的时候
  useEffect(() => {
    if (!formValue) return;
    !filterLoading && getRevenueData();
  }, [formValue, getRevenueData]);


  const title = formatMessage({ id: 'pc_monetization_mtx_revenue_platform_distribution' });
  const config = {
    bar: {
      formatterFunc: (params) => formatterFunc(params, timeType, formatMessage),
      setOtherConfigFunc: (opt) => {
        opt = {
          ...opt,
          xAxis: axisLabelFormat(opt, timeType),
        };
        return opt;
      },
    },
    line: {
      formatterFunc: (params) => formatterFunc(params, timeType, formatMessage),
      setOtherConfigFunc: (opt) => {
        opt = {
          ...opt,
          xAxis: axisLabelFormat(opt, timeType),
        };
        opt.series = opt.series?.map((item) => {
          const customColor = getSystemColor(item?.name || '');
          return {
            ...item,
            stack: '',
            showSymbol: true,
            itemStyle: {
              color: customColor ? customColor : null,
            },
          };
        });
        return opt;
      },
    },
  };

  const options = useMemo(() => {
    const data = revenueData?.mtx_revenue_by_system || [];
    const avgData = revenueData?.system_avg || {};
    const sumData = revenueData?.system_sum || {};
    // 图表数组
    const chart: any[] = [];
    const metricsList = [... new Set(data?.map(item => item?.system))];

    metricsList?.map((metric) => {
      const customColor = getSystemColor(getDynamicLocalCnEnText('os', metric, gameCode, 'en-US', selector));
      const columnObj = {
        name: getDynamicLocalCnEnText('os', metric, gameCode, getLocale(), selector),
        barCategoryGap: '50%',
        stack: 'bar',
        data: [] as any[],
        itemStyle: {
          color: customColor ? customColor : null,
        },
      };
      data?.map((item: any) => {
        if (item?.system === metric) {
          columnObj.data.push({
            x: getChartDateFormatString(timeType, item?.date, true),
            y: item?.metrics?.mtx_revenue?.value,
          });
        }
      });
      columnObj.data.length && chart.push(columnObj);
    });

    // 表头固定列
    const tableOption: TableColumn[] = [
      {
        key: 'date',
        align: 'left',
        fixed: 'left',
        width: 160,
        header: formatMessage({ id: 'monetization_revenue_Date' }),
        render: (cvm: any) => {
          const weekDay = getWeekDay(cvm?.date);
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{getChartDateFormatString(timeType, cvm?.date, true)}</span>
            {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        },
      },
    ];
    metricsList?.map(metric => {
      const metricOption: any = {
        key: metric,
        width: 140,
        align: 'right',
        header: getDynamicLocalCnEnText('os', metric, gameCode, getLocale(), selector),
      };
      tableOption.push(metricOption);
    });

    // 表数据
    const tableData: any = [];
    const downloadData: any = [];

    data?.map((item: any) => {
      const date = getChartDateFormatString(timeType, item?.date, true);
      const obj: any = { date };
      const downObj: any = { date };
      const metricObj = tableData?.find(v => v?.date === getChartDateFormatString(timeType, item?.date, true));
      const downMetricObj = downloadData?.find(v => v?.date === getChartDateFormatString(timeType, item?.date, true));
      if (metricObj) {
        metricObj[item?.system] = formatNum(item?.metrics?.mtx_revenue?.value, false);
      } else {
        obj[item?.system] = formatNum(item?.metrics?.mtx_revenue?.value, false);
        tableData.push(obj);
      }
      if (downMetricObj) {
        downMetricObj[item?.system] = formatExeclCommon(item?.metrics?.mtx_revenue?.value);
      } else {
        downObj[item?.system] = formatExeclCommon(item?.metrics?.mtx_revenue?.value);
        downloadData.push(downObj);
      }
    });

    try {
      if (JSON.stringify(sumData) !== '{}' && data?.length) {
        const sumObj: any = {
          date: formatMessage({ id: 'common_Sum' })
        };
        const downSumObj: any = {
          date: formatMessage({ id: 'common_Sum' })
        };
        Object.keys(sumData)?.map(item => {
          sumObj[item] = formatNum(sumData[item], false);
        });
        Object.keys(sumData)?.map(item => {
          downSumObj[item] = formatExeclCommon(sumData[item]);
        });
        tableData.push(sumObj);
        downloadData.push(downSumObj);
      }
      if (JSON.stringify(avgData) !== '{}' && data?.length) {
        const avgObj: any = {
          date: formatMessage({ id: 'common_Avg' })
        };
        const downAvgObj: any = {
          date: formatMessage({ id: 'common_Avg' })
        };
        Object.keys(avgData)?.map(item => {
          avgObj[item] = formatNum(avgData[item], false);
        });
        Object.keys(avgData)?.map(item => {
          downAvgObj[item] = formatExeclCommon(avgData[item]);
        });
        tableData.push(avgObj);
        downloadData.push(downAvgObj);
      }
    } catch (error) {
      console.log(error);
    }

    return {
      chart,
      table: {
        option: tableOption,
        data: tableData,
        downloadData
      },
    };
  }, [revenueData, getLocale()]);

  const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(options);
  const renderList = [{
    type: 'barChart',
    svgType: 'column-chart',
    content: formatMessage({ id: 'common_bar_chart' }),
    component: <BarChart
      height={340}
      legendData={xAxisData.length > 0 ? legendData : []}
      xAxisData={xAxisData || []}
      series={xAxisData.length > 0 ? barSeries : []}
      formatterFunc={config?.bar?.formatterFunc}
      setOtherConfigFunc={(opt: any) => {
        if (config?.bar?.setOtherConfigFunc) opt = config?.bar?.setOtherConfigFunc(opt);
        opt = xAxisHighLight(opt, {
          formatter: (params: { value: string; }) => getChartDateFormatString(timeType, params.value)
        });
        getSystemLegend(opt, selector);
        return opt;
      }}
    />
  },
  {
    type: 'lineChart',
    svgType: 'discount-chart',
    content: formatMessage({ id: 'common_line_chart' }),
    component: <LineChart
      height={340}
      legendData={xAxisData.length > 0 ? legendData : []}
      xAxisData={xAxisData || []}
      series={xAxisData.length > 0 ? lineSeries : []}
      formatterFunc={config?.line?.formatterFunc}
      setOtherConfigFunc={(opt: any) => {
        if (config?.line?.setOtherConfigFunc) opt = config?.line?.setOtherConfigFunc(opt);
        opt = xAxisHighLight(opt, {
          formatter: (params: { value: string; }) => getChartDateFormatString(timeType, params.value)
        });
        getSystemLegend(opt, selector);
        return opt;
      }}
    />
  },
  {
    type: 'sheet',
    svgType: 'sheet',
    content: formatMessage({ id: 'common_table' }),
    component: <MiniTable
      height={340}
      loading={false}
      tableOption={options?.table?.option}
      tableList={options?.table?.data}
    />
  }];

  return <CardBox
    className='register-table'
    height={340}
    bordered={false}
    titleCom={title}
    renderList={renderList || []}
    defaultAvtive={0}
    downloadClick={async () => {
      handleDownload(options?.table, title);
    }}
    isLoading={loading}
    isError={error}
    isNodata={isNodata}
  />;
}

export default PcRevenueChannelDistribution;
